import LoggedUser from "../components/LoggedUser";
import { useNavigate } from "react-router-dom";
import Logo from "./Logo";
import {
  Badge,
  Button,
  Col,
  Drawer,
  List,
  Row,
  Space,
  theme,
  Typography,
} from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import { useGuard } from "../Guard";
import LiveNotificationToasts from "./LiveNotificationToasts";
import dayjs from "dayjs";
import { SearchQuery } from "../code/search-query/search-query";
import { retry } from "../utils/http";
import {
  acknowledgeDelivery,
  getMyNotifications,
  readMyNotification,
} from "../repository/data.repository";

export interface NavigationPanelProps {}

// Keep the inbox as plain DATA (not rendered JSX) — presentation is derived in render, so the theme
// stays live and the raw title/message survive for the toast + list.
interface InboxNotification {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
  readonly createdAt: string;
  readonly isRead: boolean;
}

function NavigationPanel(props: NavigationPanelProps) {
  const { token } = theme.useToken();
  const { on, off } = useGuard();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<InboxNotification[]>([]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications],
  );

  // Lifecycle

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    const onReceiveNotification = (
      recipientUid: string,
      _notificationUid: string,
      title: string,
      message: string,
      createdAt: string,
    ) => {
      // The device just received the push — acknowledge delivery so the receipt flips to Delivered.
      acknowledgeDelivery({ uid: recipientUid }).catch(() => {});

      const item: InboxNotification = {
        uid: recipientUid,
        title,
        message,
        createdAt,
        isRead: false,
      };

      // Update the inbox (badge / drawer). The ephemeral toast is rendered by the isolated
      // <LiveNotificationToasts>, so this badge re-render can't perturb its enter animation.
      setNotifications((current) => [
        item,
        ...current.filter((n) => n.uid !== item.uid),
      ]);
    };

    on("ReceiveNotification", onReceiveNotification);
    return () => off("ReceiveNotification", onReceiveNotification);
  }, [on, off]);

  // Stable so the isolated toast component (memoized) never re-renders on our state changes.
  const openDrawer = useCallback(() => setOpen(true), []);

  // Events

  function onGoHome(): void {
    navigate("./", { replace: true });
  }

  function onNotificationRead(uid: string): void {
    // Optimistic: flip immediately, revert if the server rejects.
    setNotifications((current) =>
      current.map((n) => (n.uid === uid ? { ...n, isRead: true } : n)),
    );

    readMyNotification({ uid }).catch(() =>
      setNotifications((current) =>
        current.map((n) => (n.uid === uid ? { ...n, isRead: false } : n)),
      ),
    );
  }

  // Methods

  function loadNotifications(): void {
    const searchQuery: SearchQuery = { conditions: [] };

    setLoading(true);

    retry(() =>
      getMyNotifications({ searchQuery, pageNumber: 0, pageSize: 100 }),
    )
      .then((result) => {
        setLoading(false);
        setNotifications(
          result.data
            .map(({ uid, title, message, createdOn, isRead }) => ({
              uid,
              title,
              message,
              createdAt: createdOn,
              isRead,
            }))
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
        );
        // Delivery is client-confirmed (on render), never inferred from the fetch: explicitly
        // acknowledge any item the client now holds but hasn't yet acknowledged.
        result.data
          .filter((item) => !item.deliveredAt)
          .forEach((item) =>
            acknowledgeDelivery({ uid: item.uid }).catch(() => {}),
          );
      })
      .catch(() => setLoading(false));
  }

  return (
    <Row className="flex flex-row items-center justify-between p-2">
      {/* Company Logo */}
      <Row
        className="flex cursor-pointer flex-row items-center"
        onClick={onGoHome}
      >
        <Logo className="ml-2 mr-2 h-5" />
        <Typography.Text className="pl-2 pr-2 text-base font-bold uppercase opacity-60">
          Administrator
        </Typography.Text>
      </Row>
      {/* Logged User */}
      <Space className="flex flex-row flex-nowrap items-center">
        <Badge count={unreadCount} size="small">
          <Button
            type={"text"}
            aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
            icon={
              <Icon className="text-3xl" component={Icons.NotificationsIcon} />
            }
            onClick={() => setOpen(true)}
          />
        </Badge>
        <LoggedUser />
      </Space>
      <Drawer
        open={open}
        title="Notifications"
        placement="right"
        onClose={() => setOpen(false)}
      >
        <List
          loading={loading}
          dataSource={notifications}
          locale={{ emptyText: "You're all caught up" }}
          rowKey={(item: InboxNotification) => item.uid}
          renderItem={(item) => (
            <List.Item
              className={item.isRead ? "opacity-60" : ""}
              actions={
                item.isRead
                  ? []
                  : [
                      <Button
                        key="read"
                        size="small"
                        type="text"
                        aria-label="Mark as read"
                        icon={
                          <Icon
                            className="text-2xl"
                            style={{ color: token.colorTextDescription }}
                            component={Icons.MarkEmailReadIcon}
                          />
                        }
                        onClick={() => onNotificationRead(item.uid)}
                      />,
                    ]
              }
            >
              <List.Item.Meta
                title={item.title}
                description={
                  <Col className="flex flex-col">
                    <Typography.Text
                      style={{ color: token.colorTextDescription }}
                    >
                      {item.message}
                    </Typography.Text>
                    <Typography.Text
                      className="text-xs"
                      style={{ color: token.colorTextDescription }}
                    >
                      {dayjs(item.createdAt).format(
                        "dddd, MMMM D, YYYY, hh:mm",
                      )}
                    </Typography.Text>
                  </Col>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
      <LiveNotificationToasts onOpen={openDrawer} />
    </Row>
  );
}

export default memo(NavigationPanel);
